import { RETURN_CONDITION_LABELS, RETURN_STATUS_LABELS, type CustomerReturnView, type ReturnStatus } from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { customerText, fmt } from '../../format';
import { useSession } from '../../session';
import type { OrderSummary } from '../../types';
import { Empty, ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';

const FILTERS: readonly { label: string; status: ReturnStatus | null }[] = [
  { label: 'Tümü', status: null },
  { label: 'Beklenen', status: 'expected' },
  { label: 'Teslim alınan', status: 'received' },
  { label: 'İptal', status: 'cancelled' },
];
const STATUS_TONE: Readonly<Record<ReturnStatus, string>> = { expected: 'warn', received: 'ok', cancelled: '' };
const CONDITION_TONE = { sellable: 'ok', damaged: 'bad', defective: 'bad', opened: 'warn' } as const;

/**
 * The seller's returns: those announced (the desk recognises the parcel by its return code)
 * and those received, unit by unit with the condition the warehouse found and the reason.
 */
export function SellerReturns() {
  const { me } = useSession();
  const toast = useToast();
  const [filter, setFilter] = useState(0);
  const status = FILTERS[filter]!.status;
  const list = useLoad(() => api<CustomerReturnView[]>('GET', `/api/returns${status ? `?status=${status}` : ''}`), [status], 30_000);
  const [announcing, setAnnouncing] = useState(false);
  const canWrite = me.role !== 'viewer';

  const cancel = async (r: CustomerReturnView) => {
    try {
      await api('POST', `/api/returns/${r.id}/cancel`);
      toast(`${r.rmaNumber} iptal edildi`, 'ok');
      void list.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  return (
    <>
      <div className="row">
        {FILTERS.map((f, i) => (
          <button key={f.label} className={`chip select${filter === i ? ' on' : ''}`} onClick={() => setFilter(i)}>
            {f.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {canWrite && (
          <button className="btn primary" onClick={() => setAnnouncing(true)}>
            <Icon name="plus" /> İade bildir
          </button>
        )}
      </div>
      <p className="muted" style={{ margin: 0 }}>
        Müşteriniz bir siparişi iade ettiğinde iade kargo kodunu bildirin: paket depoya ulaşınca hemen tanınır. Depo her ürünü inceler; sağlam ürünler
        satılabilir stoğa döner, hasarlı, kusurlu veya açılmış ürünler gerekçesiyle ayrılır.
      </p>
      {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
      {!list.data ? (
        <Spinner />
      ) : list.data.length === 0 ? (
        <section className="card">
          <Empty title="İade yok" />
        </section>
      ) : (
        list.data.map((r) => (
          <section key={r.id} className="card">
            <div className="card-h">
              <h2 className="mono">{r.rmaNumber}</h2>
              <span className={`chip ${STATUS_TONE[r.status]}`}>{RETURN_STATUS_LABELS[r.status]}</span>
              <span className="sub">
                Sipariş <b className="mono">{r.orderNumber}</b>
                {(r.customerName || r.personalDataErasedAt) && ` · ${customerText(r)}`}
              </span>
              <span className="spacer" />
              {r.status === 'expected' && canWrite && (
                <button className="btn sm" onClick={() => void cancel(r)}>
                  İptal et
                </button>
              )}
            </div>
            <div className="card-b stack">
              <div className="muted">
                {r.returnTrackingNumber ? (
                  <>
                    İade kargo kodu <b className="mono">{r.returnTrackingNumber}</b> ·{' '}
                  </>
                ) : null}
                {r.customerReason && <>Müşterinin nedeni: {r.customerReason} · </>}
                {r.receivedAt ? `${fmt.dateTime(r.receivedAt)} teslim alındı` : `${fmt.dateTime(r.createdAt)} bildirildi`}
              </div>
              {r.lines.length > 0 && (
                <div className="table-wrap">
                  <table className="t">
                    <thead>
                      <tr>
                        <th>Ürün</th>
                        <th className="num">Adet</th>
                        <th>Durum</th>
                        <th>Gerekçe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.lines.map((l, i) => (
                        <tr key={i}>
                          <td>
                            {l.name} <span className="mono muted">{l.sku}</span>
                            {l.lotNo && <div className="muted" style={{ fontSize: 12 }}>Lot {l.lotNo}</div>}
                          </td>
                          <td className="num">{l.qty}</td>
                          <td>
                            <span className={`chip ${CONDITION_TONE[l.condition]}`}>{RETURN_CONDITION_LABELS[l.condition]}</span>
                            <div className="muted" style={{ fontSize: 12 }}>
                              {l.condition === 'sellable' ? 'Satışa döner' : 'Satışa kapalı'}
                            </div>
                          </td>
                          <td>{l.reason ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {r.note && <div className="muted">Depo notu: {r.note}</div>}
            </div>
          </section>
        ))
      )}
      {announcing && (
        <AnnounceReturnDialog
          order={null}
          onClose={() => setAnnouncing(false)}
          onDone={(r) => {
            setAnnouncing(false);
            toast(`${r.rmaNumber} bildirildi`, 'ok');
            if (status !== null && status !== 'expected') setFilter(1);
            void list.reload();
          }}
        />
      )}
    </>
  );
}

/** Announces a return: the shipped order (given, or searched), the code on the return parcel and the buyer's reason. */
export function AnnounceReturnDialog(props: {
  order: { readonly id: string; readonly orderNumber: string } | null;
  onClose: () => void;
  onDone: (r: CustomerReturnView) => void;
}) {
  const [order, setOrder] = useState(props.order);
  const [search, setSearch] = useState('');
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const term = search.trim();
  const found = useLoad(
    () => (order || term.length < 2 ? Promise.resolve([]) : api<OrderSummary[]>('GET', `/api/orders?status=shipped,delivered&search=${encodeURIComponent(term)}&limit=10`)),
    [order, term],
  );

  const submit = async () => {
    if (!order) return;
    setBusy(true);
    setError(null);
    try {
      props.onDone(
        await api<CustomerReturnView>('POST', '/api/returns', { orderId: order.id, returnTrackingNumber: code.trim() || null, customerReason: reason.trim() || null }),
      );
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="İade bildir"
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={!order || busy} onClick={() => void submit()}>
            Bildir
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        {order ? (
          <div className="row">
            <span>
              Sipariş <b className="mono">{order.orderNumber}</b>
            </span>
            {!props.order && (
              <button className="btn sm ghost" onClick={() => setOrder(null)}>
                Değiştir
              </button>
            )}
          </div>
        ) : (
          <div className="field">
            <label htmlFor="ret-search">Kargolanmış sipariş</label>
            <input id="ret-search" className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Sipariş no veya takip no" autoFocus />
            {found.data && found.data.length > 0 && (
              <ul className="hit-list" style={{ marginTop: 8 }}>
                {found.data.map((o) => (
                  <li key={o.id}>
                    <button className="hit-row" onClick={() => setOrder({ id: o.id, orderNumber: o.orderNumber })}>
                      <b className="mono">{o.orderNumber}</b> <span>{customerText(o)}</span> <span className="muted">{fmt.dateTime(o.orderedAt)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {term.length >= 2 && found.data?.length === 0 && <span className="hint">Bu aramaya uyan kargolanmış sipariş yok.</span>}
          </div>
        )}
        <div className="field">
          <label htmlFor="ret-code">İade kargo kodu</label>
          <input id="ret-code" className="input mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Pazaryerinin verdiği iade kodu" />
          <span className="hint">Kolinin üzerindeki kod; depo paketi bununla tanır. Bilmiyorsanız boş bırakın.</span>
        </div>
        <div className="field">
          <label htmlFor="ret-reason">Müşterinin iade nedeni</label>
          <input id="ret-reason" className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Örn. beden uymadı" />
        </div>
      </div>
    </Modal>
  );
}
